import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { RestaurantAttendance, RestaurantAttendanceCell, RestaurantAttendanceGrid as Grid,
  RestaurantAttendanceRow } from '../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../utils/apiError';
import { formatCivilDate, formatCivilDateTime, formatHours } from '../utils/restaurantPersonnelFormat';
import RestaurantAttendanceEditor from './RestaurantAttendanceEditor';

const ukraineMonth = () => new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit',
}).format(new Date());
const RestaurantAttendanceGrid: React.FC<{
  canEdit: boolean;
  refreshKey: number;
  onSaved: () => void;
}> = ({ canEdit, refreshKey, onSaved }) => {
  const [month, setMonth] = useState(ukraineMonth);
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<Grid['departments']>([]);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState<Grid | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<{ row: RestaurantAttendanceRow; cell: RestaurantAttendanceCell } | null>(null);
  const [original, setOriginal] = useState<RestaurantAttendance | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const requestKey = `${month}|${departmentId}|${search}|${page}|${pageSize}|${reload}|${refreshKey}`;
  const [loadedKey, setLoadedKey] = useState('');
  const ready = loadedKey === requestKey;

  useEffect(() => {
    const controller = new AbortController();
    setResult(null);
    setSelected(null);
    setError('');
    const [year, number] = month.split('-').map(Number);
    void api.get<Grid>('/restaurant-personnel/attendance-grid', {
      signal: controller.signal, params: { year, month: number, departmentId: departmentId || undefined, search, page, pageSize },
    }).then(response => {
      if (controller.signal.aborted) return;
      setResult(response.data);
      setDepartments(response.data.departments);
      setLoadedKey(requestKey);
    }).catch(failure => {
      if (!controller.signal.aborted)
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити місячний табель.'));
    });
    return () => controller.abort();
  }, [month, departmentId, search, page, pageSize, reload, refreshKey, requestKey]);

  useEffect(() => { if (selected) heading.current?.focus(); }, [selected]);
  const chooseEntry = (item: RestaurantAttendance | null) => {
    setOriginal(item);
    setEditorKey(value => value + 1);
  };
  const rows = ready ? result?.employees.items ?? [] : [];
  const totals = rows.reduce((sum, row) => ({
    hours: sum.hours + row.hoursWorked, weighted: sum.weighted + row.weightedHours,
  }), { hours: 0, weighted: 0 });

  return <section aria-label="Місячний табель" className="restaurant-attendance-grid">
    <h2>Місячний табель працівників</h2>
    <details>
      <summary>Детальніше про дати та підрахунок табеля</summary>
      <p>Робоча дата: від першого числа включно до наступного місяця виключно.
        Відкриті інтервали позначено окремо; години не дораховуються автоматично.</p>
    </details>
    <div className="restaurant-personnel__form">
      <label>Місяць таблиці<input type="month" required min="2000-01" max="2200-12" value={month} disabled={saving}
        onChange={event => {
          if (/^(20\d{2}|21\d{2}|2200)-(0[1-9]|1[0-2])$/.test(event.target.value)) {
            setMonth(event.target.value); setDepartmentId(''); setDepartments([]); setPage(1);
          }
        }} /></label>
      <label>Підрозділ табелю<select value={departmentId} disabled={saving} onChange={event => {
        setDepartmentId(event.target.value); setPage(1);
      }}>
        <option value="">Усі підрозділи</option>
        {departments.map(unit => <option key={unit.departmentId} value={unit.departmentId}>{unit.name}</option>)}
      </select></label>
      <label>Працівників на сторінці<select value={pageSize} disabled={saving} onChange={event => {
        setPageSize(Number(event.target.value)); setPage(1);
      }}>{[10, 25, 50, 100].map(size => <option key={size}>{size}</option>)}</select></label>
    </div>
    <form onSubmit={event => { event.preventDefault(); setSearch(query.trim()); setPage(1); }}>
      <label>Пошук у табелі<input type="search" maxLength={200} value={query} disabled={saving}
        onChange={event => setQuery(event.target.value)} /></label>
      <div className="restaurant-personnel__actions">
        <button disabled={saving} type="submit">Знайти в табелі</button>
        <button disabled={saving} type="button" onClick={() => setReload(value => value + 1)}>Оновити місячний табель</button>
      </div>
    </form>
    {error && <p role="alert">{error}</p>}
    {(!result || !ready) && !error && <p role="status">Завантаження місячного табелю…</p>}
    {ready && result && <>
      <p role="status">Працевлаштувань у табелі: {result.employees.total} · Сторінка {result.employees.page}</p>
      <p>Підсумки цієї сторінки: {formatHours(totals.hours)} фактичних · {formatHours(totals.weighted)} зарахованих (з коефіцієнтами).</p>
      {!rows.length ? <p>Працевлаштувань за обраними умовами немає.</p> :
        <div className="restaurant-personnel__scroll" tabIndex={0} role="region" aria-label="Таблиця працівники та дні">
          <table>
            <caption>Табель {month} · у клітинках зараховані години; натисніть для перегляду інтервалів</caption>
            <thead><tr><th scope="col">Працівник</th>
              {result.days.map(day => <th scope="col" key={day}><time dateTime={day}>{day.slice(-2)}</time></th>)}
              <th scope="col">Фактичні, год</th><th scope="col">Зараховані, год</th>
            </tr></thead>
            <tbody>{rows.map(row => <tr key={row.employmentId}>
              <th scope="row">{row.fullName}<small>{row.position} · {row.departmentName || 'Без підрозділу'}</small>
                {!row.useAttendance && <small>Табелювання вимкнено</small>}</th>
              {row.cells.map(cell => {
                const open = cell.entries.some(item => !item.checkOutAt);
                return <td key={cell.workDate}>
                  <button type="button" disabled={saving || (!cell.employed && !cell.entries.length)}
                    aria-label={`${row.fullName}, ${formatCivilDate(cell.workDate)}: ${cell.entries.length} інтервалів${open ? ', відкрита зміна' : ''}`}
                    onClick={event => {
                      trigger.current = event.currentTarget;
                      chooseEntry(null); setSelected({ row, cell });
                    }}>
                    {cell.entries.length ? formatHours(cell.weightedHours, 2, false) : cell.employed ? '—' : 'Не працює'}
                    {cell.entries.length > 1 && <small>{cell.entries.length} інтерв.</small>}
                    {open && <small>Відкрито</small>}
                  </button>
                </td>;
              })}
              <td>{formatHours(row.hoursWorked, 4, false)}</td><td>{formatHours(row.weightedHours, 4, false)}</td>
            </tr>)}</tbody>
          </table>
        </div>}
      <div className="restaurant-personnel__actions">
        <button type="button" disabled={saving || page === 1} onClick={() => setPage(page - 1)}>Попередні працівники табелю</button>
        <button type="button" disabled={saving || page * pageSize >= result.employees.total}
          onClick={() => setPage(page + 1)}>Наступні працівники табелю</button>
      </div>
    </>}
    {ready && selected && <section aria-label="Інтервали клітинки">
      <h3 ref={heading} tabIndex={-1}>{selected.row.fullName} · {formatCivilDate(selected.cell.workDate)}</h3>
      <p>Фактичні: {formatHours(selected.cell.hoursWorked)} · Зараховані: {formatHours(selected.cell.weightedHours)}</p>
      {!selected.cell.entries.length && <p>Інтервалів за цей день немає.</p>}
      <div className="restaurant-personnel__list">{selected.cell.entries.map(item =>
        <div key={item.id}>
          <p>{formatCivilDateTime(item.checkInAt)} — {item.checkOutAt ? formatCivilDateTime(item.checkOutAt) : 'Відкрито'}
            {' · '}{formatHours(item.hoursWorked)} · коеф. {item.hourCoefficient} · {item.comment}</p>
          {canEdit && selected.row.useAttendance && <button type="button" disabled={saving} aria-pressed={original?.id === item.id}
            title={`ID інтервалу: ${item.id}`} onClick={() => chooseEntry(item)}>Редагувати інтервал</button>}
        </div>)}</div>
      {canEdit && selected.row.useAttendance && (selected.cell.employed || original) &&
        <RestaurantAttendanceEditor key={editorKey} employmentId={selected.row.employmentId}
          original={original} defaultDate={selected.cell.workDate} onSavingChange={setSaving}
          onDeleted={() => { setSelected(null); setReload(value => value + 1); onSaved(); }}
          onNew={selected.cell.employed ? () => chooseEntry(null) : undefined}
          onSaved={() => { setSelected(null); setReload(value => value + 1); onSaved(); }} />}
      {!selected.row.useAttendance && <p>Редагування недоступне: табелювання вимкнено у картці.</p>}
      <button type="button" disabled={saving} onClick={() => { setSelected(null); trigger.current?.focus(); }}>Закрити клітинку</button>
    </section>}
  </section>;
};
export default RestaurantAttendanceGrid;
